<script setup lang="ts">
  import { reactive, onMounted, onBeforeUnmount, watch } from "vue";
  import { useBarcode, useReadRfid } from "@plugins";
  import {
    ResponseCode,
    wtfirApi,
    WTFIRInputType,
    WTFIRTypeData,
    loadExecutorsApi,
    orderHistoryWidthCursorApi,
    shopZoneListWithCursorApi,
    orderListWidthCursorApi,
    type Order,
  } from "@src/api";
  import { StatusBar } from "@src/components";
  import { useCarousel, useEventsPolling, useProfileOptions } from "@src/hooks";
  import { useOrderListStore, useExecutorListStore, useShopZoneListStore, useWidgetsStore } from "@src/store";
  import { Widgets } from "@src/widgets";
  import { default as MainPageHeader } from "./MainPageHeader.vue";
  import { default as MainPageSwitchLocation } from "./MainPageSwitchLocation.vue";
  import { default as MainPageMenuShopfloor } from "./MainPageMenuShopfloor.vue";
  import { default as MainPageMenuStore } from "./MainPageMenuStore.vue";
  import { default as MainPageUserInfoModal } from "./MainPageUserInfoModal.vue";

  const menuItems = ["shopfloor", "store"];
  const state = reactive<{ user_info_modal_visible: boolean }>({ user_info_modal_visible: false });

  // listRef, wrapperRef - выглядит что они не используются,
  // но если их убрать, то перестанет работать горизонтальный скрол
  const {
    listRef,
    wrapperRef,
    state: carousel_state,
    goToIndex,
    handleStart,
    handleMove,
    handleEnd,
    getItemStyle,
  } = useCarousel({
    itemCount: menuItems.length,
  });

  const barcode = useBarcode();
  const readRfid = useReadRfid();
  const options = useProfileOptions();
  const events = useEventsPolling();
  const orderListStore = useOrderListStore();
  const executorListStore = useExecutorListStore();
  const shopZoneListStore = useShopZoneListStore();
  const widgets = useWidgetsStore();

  const handleScanData = async (data: string, input_type: WTFIRInputType) => {
    const result = await wtfirApi({ data, input_type });

    if (result.code !== ResponseCode.OK || result.type_data === WTFIRTypeData.unknown) return;

    if (
      [WTFIRTypeData.barcode, WTFIRTypeData.cis, WTFIRTypeData.epc, WTFIRTypeData.qr].includes(result.type_data) &&
      result.items.length > 0
    ) {
      widgets.open("card_item", { item_id: result.items.at(0) });

      return;
    }

    if (result.type_data !== WTFIRTypeData.action) return;

    if (result.action.action === "rb_ge_write_epc") {
      widgets.open("write_random_epc");
    } else if (result.action.action === "write_epc") {
      widgets.open("label_printing_ut", { epc: result.action.epc });
    } else if (result.action.action === "repair_alarm") {
      widgets.open("repair_alarm");
    }

    handleUnsubscribe();
  };

  const handleAddListener = () => {
    barcode.subscribe((data) => handleScanData(data, WTFIRInputType.barcode));
    readRfid.subscribe((data) => handleScanData(data.value, WTFIRInputType.rfid));
  };

  const handleUnsubscribe = () => {
    barcode.unsubscribe();
    readRfid.unsubscribe();
  };

  const loadHistory = async (): Promise<Order[] | undefined> => {
    if (orderListStore.isOrderHistoryLoaded) return;

    const orders = await orderHistoryWidthCursorApi();
    orderListStore.setHistory(orders);

    return orders;
  };

  const loadShopZoneList = async () => {
    const shop_zone_list = await shopZoneListWithCursorApi();
    shopZoneListStore.add(shop_zone_list);
  };

  const loadOrders = async (): Promise<Order[] | undefined> => {
    if (orderListStore.isOrderListLoaded) return;

    const orders = await orderListWidthCursorApi();
    orderListStore.set(orders);

    return orders;
  };

  const getExecutorsIds = (orders: Order[]) => {
    const executorsIds = new Set<string>();

    orders.forEach((order) => {
      if (order.executed_by) executorsIds.add(order.executed_by);
    });

    return Array.from(executorsIds);
  };

  const loadExecutorList = async (executorsIds: string[]) => {
    if (executorsIds.length === 0) return;

    const executorsResult = await loadExecutorsApi({ user_id: executorsIds });

    if (executorsResult.code !== ResponseCode.OK) return;

    executorListStore.add(executorsResult.executors);
  };

  watch(
    () => widgets.unsubscribe_main_menu,
    (new_state) => {
      if (new_state) handleUnsubscribe();
      else handleAddListener();
    },
  );

  onMounted(async () => {
    handleAddListener();

    if (options.hasPermission("mobile.order_list")) {
      events.startPolling({
        keys: [
          ["order", "shop", options.profile.value.current_user.shop_id],
          ["shop", options.profile.value.current_user.shop_id],
        ],
      });

      const orders = await loadOrders();
      const history = await loadHistory();

      if (orders || history) {
        await loadExecutorList(getExecutorsIds([...(orders || []), ...(history || [])]));
      }
    }

    if (options.hasPermission("mobile.shop_zone_list")) {
      await loadShopZoneList();
    }
  });

  onBeforeUnmount(handleUnsubscribe);
</script>

<template>
  <div class="main-page" data-testid="main-page">
    <StatusBar />
    <MainPageHeader
      @click:avatar="
        () => {
          state.user_info_modal_visible = true;
          handleUnsubscribe();
        }
      "
      @click:search="
        () => {
          widgets.open('search');
          handleUnsubscribe();
        }
      "
    />
    <div class="main-page__content">
      <div ref="wrapperRef" class="main-page__menu-wrapper">
        <div
          ref="listRef"
          class="main-page__menu-list"
          @touchstart="handleStart"
          @touchmove="handleMove"
          @touchend="handleEnd"
        >
          <MainPageMenuShopfloor :style="getItemStyle(0)" />
          <MainPageMenuStore :style="getItemStyle(1)" />
        </div>
      </div>
      <div class="main-page__switch-menu-container">
        <MainPageSwitchLocation
          :location="menuItems[carousel_state.activeIndex]"
          @click:shopfloor="goToIndex(0)"
          @click:store="goToIndex(1)"
        />
      </div>
    </div>
  </div>
  <MainPageUserInfoModal
    v-if="state.user_info_modal_visible"
    @close="
      () => {
        state.user_info_modal_visible = false;
        handleAddListener();
      }
    "
  />
  <Widgets />
</template>

<style scoped>
  .main-page {
    display: flex;
    flex-direction: column;
    flex: 1;
    overflow: hidden;
    position: relative;
    background-color: var(--sf-base-background-primary);
  }

  .main-page__content {
    flex: 1;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  .main-page__menu-wrapper {
    display: flex;
    flex-direction: column;
    flex: 1;
    user-select: none;
    overflow-y: auto;
    overflow-x: hidden;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
  }

  .main-page__menu-list {
    display: flex;
    flex: 1;
    transition: transform 0.4s cubic-bezier(0.2, 1.25, 0.6, 1);
    will-change: transform;
    height: 100%;
  }

  .main-page__menu-list > * {
    height: 100%;
    min-height: 0;
  }

  .main-page__switch-menu-container {
    position: absolute;
    bottom: 16px;
    left: 0;
    right: 0;
    display: flex;
    justify-content: center;
    align-items: center;
  }
</style>
